import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { ShieldAlert, Loader2, ArrowRight } from "lucide-react";

/**
 * Hard-stop screen shown when a security-hold has been opened for the
 * current candidate on this section (cross-user face/voice mismatch, or
 * ambient-voice detected mid-attempt). Polls the hold status every 5 s
 * and reacts to the admin's resolution:
 *   - approved_resume: navigates back to the section entry so /next runs
 *     again and lets the candidate continue.
 *   - approved_reset:  same, but the server has already forfeited any
 *     in-progress attempt so /next creates a fresh one.
 *   - rejected: candidate stays blocked with a final message.
 * The component sits inside the section pages, replacing the whole page
 * body when a hold is active.
 */
export default function SecurityHardStop({ holdId, reason, onResolvedResume, onResolvedReset }) {
  const [hold, setHold] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!holdId) return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get(`/participant/security/holds/${holdId}`);
        if (!alive) return;
        setHold(r.data);
        if (r.data.status === "approved_resume" && onResolvedResume) onResolvedResume();
        if (r.data.status === "approved_reset" && onResolvedReset) onResolvedReset();
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.detail || "Unable to check hold status.");
      }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [holdId, onResolvedResume, onResolvedReset]);

  const status = hold?.status || "pending";
  const reasonLabel = {
    face_mismatch: "the face on camera didn't match your enrolled selfie",
    voice_mismatch: "the voice on the mic didn't match your enrolled voice",
    ambient_voice: "another voice was detected in the room during your attempt",
  }[hold?.reason || reason] || "an integrity check was triggered";

  return (
    <div className="max-w-2xl mx-auto p-6 mt-10" data-testid="security-hard-stop">
      <div className="card border-l-4 border-[#EE2737] p-8">
        <div className="flex items-start gap-4">
          <ShieldAlert size={40} className="text-[#EE2737] flex-shrink-0" />
          <div>
            <div className="overline text-[#EE2737]">SECURITY HOLD · ATTEMPT PAUSED</div>
            <h1 className="text-2xl font-heading font-black mt-1 mb-3">
              This attempt has been paused pending admin review.
            </h1>
            <p className="text-sm text-[#525252] leading-relaxed mb-4">
              We paused your attempt because <b>{reasonLabel}</b>. Your assessment
              admin has been notified and will review shortly. Please <b>do not close this window</b>
              — as soon as the admin approves, we'll route you back to the section automatically.
            </p>

            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 text-xs space-y-1.5">
              <div className="flex justify-between">
                <span className="text-[#878787] uppercase tracking-widest font-bold">Hold ID</span>
                <span className="font-mono text-[#1A1A24]">{holdId?.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#878787] uppercase tracking-widest font-bold">Status</span>
                <span className="font-semibold text-[#1A1A24] flex items-center gap-1.5">
                  {status === "pending" && <><Loader2 size={12} className="animate-spin" /> Waiting for admin</>}
                  {status === "approved_resume" && <span className="text-[#059669]">Approved — resuming…</span>}
                  {status === "approved_reset" && <span className="text-[#F59E0B]">Fresh attempt approved…</span>}
                  {status === "rejected" && <span className="text-[#EE2737]">Rejected</span>}
                </span>
              </div>
            </div>

            {status === "rejected" && (
              <p className="text-sm text-[#991B1B] mt-4 leading-relaxed">
                Your attempt was rejected by the admin. Please contact your assessment
                admin to discuss next steps.
              </p>
            )}

            {err && <p className="text-xs text-[#EE2737] mt-3">{err}</p>}

            <div className="mt-6 flex items-center gap-3">
              <Link to="/participant/salescert" className="btn btn-secondary text-xs" data-testid="security-hard-stop-hub">
                <ArrowRight size={12} /> Back to certification hub
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
